<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { X } from 'lucide-vue-next';

import { freeze, unfreeze } from '@motillo/ui-scroll-freeze';

import useContext from '@/composables/useContext';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import useSearch from '@/composables/useSearch';
import useEventListener from '@/composables/useEventListener';

import SearchBar from '@/components/search/SearchBar.vue';
import SearchResult from '@/components/search/SearchResult.vue';

const { route } = useContext();
const { websiteText } = useWebsiteTexts();
const { searchQuery, error, searchResult, clearSearch } = useSearch();
const { useEmitterEventListener } = useEventListener();

const isOpen = ref(false);

const zeroHitsCaption = computed(() => {
  if (searchQuery.value && searchResult.value?.products?.length === 0) {
    return websiteText('search__no_hits_caption').value?.replace('{0}', searchQuery.value);
  }
  return '';
});

function open() {
  freeze('Search');
  isOpen.value = true;
}

function close() {
  unfreeze('Search');
  isOpen.value = false;
  clearSearch();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && isOpen.value) {
    close();
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
  watch(
    () => route.pathname,
    () => close(),
  );
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
});

useEmitterEventListener('Search:Open', open);
</script>

<template>
  <Transition name="search-fade">
    <div v-if="isOpen" class="fixed inset-0 z-[1000] overflow-y-auto bg-background">
      <button
        type="button"
        class="btn-bare fixed right-4 top-4 z-[1001] flex items-center gap-1.5 p-2 text-muted-foreground transition-colors hover:text-foreground tablet-up:right-6 tablet-up:top-6"
        :aria-label="websiteText('getmygift__search__close').value || 'Stäng'"
        @click="close()"
      >
        <span class="text-sm font-normal uppercase tracking-wide">
          {{ websiteText('getmygift__search__close').value || 'Stäng' }}
        </span>
        <X class="h-7 w-7" />
      </button>

      <div class="relative mx-auto max-w-[var(--site-max-width)] px-4">
        <div class="mx-auto max-w-2xl pb-8 pt-16 text-center tablet-up:pt-20">
          <h2 class="m-0 mb-8 text-xl !font-light tracking-wide text-foreground tablet-up:text-2xl">
            {{ websiteText('getmygift__search__pagetitle').value }}
          </h2>
          <SearchBar />
        </div>

        <div class="pb-16">
          <SearchResult />
          <p v-if="zeroHitsCaption" class="text-center text-xs text-muted-foreground">
            {{ zeroHitsCaption }}
          </p>
          <p v-if="error" class="text-center text-xs text-warning">
            {{ websiteText('getmygift__search__unknownerror').value }}
          </p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style>
.search-fade-enter-active,
.search-fade-leave-active {
  transition: opacity 0.2s ease;
}
.search-fade-enter-from,
.search-fade-leave-to {
  opacity: 0;
}
</style>
